import { extractMessage } from '@/helpers/errorHelper';
import { dashboardRange, filterBadges, weekDays } from '@/views/graficas/dashboardData';
import { getDateLocaleNatural } from '@/utils/DateUtil';

/**
 * Carga de un widget del Dashboard: `loading`, `error` y descarte de respuestas viejas.
 *
 * Cada widget pide lo suyo y falla por separado: un error en uno no vacía los demás. Si el rango cambia a mitad de
 * una petición solo cuenta la última respuesta (`requestId`).
 *
 *   created() { this.reload(); },
 *   methods: { reload() { return this.loadWidget(() => Service.x(), (data) => { this.data = data; }); } }
 *
 * `apply(null)` se llama cuando la petición falla, para que el widget deje datos vacíos y no los de antes.
 */
export const dashboardWidgetMixin = {
    data() {
        return { loading: false, error: '', requestId: 0 };
    },
    methods: {
        async loadWidget(fetcher, apply) {
            const request = ++this.requestId;
            this.loading = true;
            this.error = '';
            try {
                const resp = await fetcher();
                if (request !== this.requestId) return;
                apply(resp ? resp.data : null);
            } catch (error) {
                if (request !== this.requestId) return;
                apply(null);
                this.error = extractMessage(error);
            } finally {
                if (request === this.requestId) this.loading = false;
            }
        }
    }
};

/**
 * Filtros del tablero (sucursal y producto) para los widgets: props `sucursalId` / `productoId` (vacíos = todos), sus
 * nombres para rotular, y el objeto `filters` que se manda al servicio. Recarga con `reload()` cuando cambian.
 *
 * Cada widget declara qué filtros respeta con la opción `filterScope` (objeto, o función si depende de sus props):
 *
 *   filterScope: {
 *       sucursal: true,
 *       producto: false,
 *       reasons: { producto: 'Por qué no aplica' },            // se muestra en el chip "Producto: no aplica"
 *       partial: { producto: [{ label: 'Sin X', reason: '...' }] } // lo que cambia aunque el filtro sí aplique
 *   }
 *
 * `filters` solo lleva los que el widget respeta (no recarga por los demás) y `filterBadges` dice, para `FilterBadges`,
 * qué filtro se aplicó y cuál no (y por qué), para que nadie lea un total filtrado donde no lo es.
 */
export const dashboardFilterMixin = {
    props: {
        sucursalId: { type: [Number, String], default: null },
        productoId: { type: [Number, String], default: null },
        sucursalNombre: { type: String, default: '' },
        productoNombre: { type: String, default: '' }
    },
    computed: {
        honors() {
            const option = this.$options.filterScope;
            const declared = typeof option === 'function' ? option.call(this) : option;
            return { sucursal: true, producto: true, reasons: {}, partial: {}, ...declared };
        },
        filters() {
            return {
                sucursalId: (this.honors.sucursal && Number(this.sucursalId)) || null,
                productoId: (this.honors.producto && Number(this.productoId)) || null
            };
        },
        filterKey() {
            return `${this.filters.sucursalId}|${this.filters.productoId}`;
        },
        filterBadges() {
            return filterBadges(
                this.honors,
                { sucursalId: Number(this.sucursalId) || null, productoId: Number(this.productoId) || null },
                { sucursalNombre: this.sucursalNombre, productoNombre: this.productoNombre }
            );
        }
    },
    watch: {
        filterKey() {
            this.reload();
        }
    }
};

/**
 * Rango del tablero para los widgets que lo usan: props `start` / `end` (`yyyy-MM-dd`, vacíos = todas las fechas),
 * acotado a lo que acepta el backend, y las fechas en `dd-MM-yyyy` que el backend espera. Recarga con `reload()`
 * cuando cambia el rango (el widget debe definirlo).
 */
export const dashboardRangeMixin = {
    props: {
        start: { type: String, default: '' },
        end: { type: String, default: '' }
    },
    computed: {
        range() {
            return dashboardRange(this.start, this.end);
        },
        clamped() {
            return this.range.clamped;
        },
        // Los días del rango si es de una semana o menos (las gráficas por día los rotulan Lunes, Martes...); si no, null.
        week() {
            return weekDays(this.range.start, this.range.end);
        },
        startNatural() {
            return getDateLocaleNatural(this.range.start);
        },
        endNatural() {
            return getDateLocaleNatural(this.range.end);
        },
        rangeKey() {
            return `${this.range.start}|${this.range.end}`;
        }
    },
    watch: {
        rangeKey() {
            this.reload();
        }
    }
};
